/* global angular */

/**
 * ContentURL Service
 *
 * Retrieves a URL for a content. The URL is retrieved by reading a named configuration
 * block from this service's configuration, and then get the content using the variant
 * configured there. The variant is expected to return a content having an aspect named
 * the same as the variant (or aspectName from the configuration). The aspect's data
 * must contain the field 'contentPath' that will be of a desired format, and prepended
 * to the 'baseURL' read from the configuration.
 *
 * If the configuration block has a property 'type' with the value 'preview' an additional
 * 'previewData' search parameter is added containing a base64 encoded string with information
 * about the alias of the content, the workspace that it is from as well as a temporary
 * access token for that workspace.
 *
 * Config example:
 * @example
 * "ContentURLService": {
 *   "myWebSite": {
 *     "baseURL": "http://myWebSite.org",
 *     "variant": "webView"
 *   },
 *   "myWebSitePreview": {
 *     "baseURL": "http://myWebSite.org/preview",
 *     "type": "preview",
 *     "variant": "webPreview"
 *   }
 * }
 *
 * Usage example:
 * @example
 * const alias = 'contentid/myContentAlias'
 * ContentURLService.getContentURL ({
 *   alias: 'contentid/myContentAlias',
 *   name: 'myWebSite'
 * }).then ({ url } => window.location = url)
 *
 * // -> Could result in opening 'http://myWebSite.org/article/contentid/myContentAlias'
 * //    (depending on the result from the variant).
 * // ...
 * ContentURLService.getContentURL ({
 *   alias: 'contentid/myContentAlias',
 *   name: 'myWebSitePreview'
 * }).then ({ url } => window.open (url))
 *
 * // open a preview in a new window...
 *
 */
angular.module('atex.aceui.service').factory('ContentURLService',
  ['AuthenticationService', 'ApplicationConfigurationService', 'WorkspaceService', 'ContentService',

    function (AuthenticationService, ApplicationConfigurationService, WorkspaceService, ContentService) {
      const NAME = 'ContentURLService';
      const AceError = atex.aceui.error;
      const Utils = atex.aceui.ObjectUtils;
      const TYPE_PREVIEW = 'preview';

      const verifyConfig = (config) => {
        if (!config) {
          throw AceError.Error ({
            cause: 'Configuration missing',
            origin: NAME,
            severity: AceError.severities.MAJOR,
            type: AceError.types.CONFIG_ERROR
          });
        } else if (!config.baseURL || !config.variant) {
          console.log ('config:', config);

          let missing = [config.baseURL ? undefined : '\'baseURL\'', config.variant ? undefined : '\'variant\''].join (' ');

          throw AceError.Error ({
            cause: `Missing required configuration: ${missing}`,
            origin: NAME,
            severity: AceError.severities.MAJOR,
            type: AceError.types.CONFIG_ERROR
          });
        }
        return config;
      };

      const fetchConfig = async (name) => {
        const serviceConfig = await ApplicationConfigurationService.getServiceConfigurationFor ('ContentURLService');
        return verifyConfig (serviceConfig[name]);
      };

      const fetchContent = async ({ alias, variant, workspace }) => {
        try {
          const service = workspace ? WorkspaceService : ContentService;
          return await service.getContent({alias, variant, workspace});
        } catch (error) {
          if (error.type === AceError.types.NOT_FOUND) {
            const status = error.rootCause && error.rootCause.responseJSON && error.rootCause.responseJSON.statusCode;

            if (status === '40460') {
              throw AceError.Error ({
                cause: `The variant '${variant}' does not exist.`,
                origin: NAME,
                rootCause: error,
                severity: AceError.severities.MAJOR,
                type: AceError.types.NOT_FOUND
              });
            }
            if (status === '40461') {
              throw AceError.Error ({
                cause: `The content '${alias}' do not have a representation in the variant '${variant}'.`,
                origin: NAME,
                rootCause: error,
                severity: AceError.severities.MAJOR,
                type: AceError.types.NOT_FOUND
              });
            }
          }

          throw error;
        }
      }

      const getPath = (aspect) => `content/aspects/${aspect}/contentPath`;

      return {
        /**
         * Get a URL for a content.
         *
         * @param {string} alias - An alias of the content to use
         * @param {Object} config - A config block overriding any named configuration
         * @param {string} name - The property name of a configuration block of the service configuration
         *                        (ignored if 'config' was provided)
         * @param {string} workspace - Fetch the content through this workspace
         *
         * @return { url: string } - An object with one property 'url' containing the result
         * @throws atex.aceui.error.Error
         */
        async getContentURL ({ alias, config, name, workspace }) {
          config = config ? verifyConfig(config) : await fetchConfig (name);

          const response = await fetchContent ({ alias, variant: config.variant, workspace });
          const baseURL = config.baseURL;
          const path = Utils.getByPath (response, getPath (config.aspectName || config.variant));

          var url;
          var absoluteUrlPattern = /^https?:\/\//i;

          if (absoluteUrlPattern.test(path)) {
            // Absolute URLs we use as-is
            url = new URL (path);
          } else {
            url = new URL (baseURL);

            var realPath = path;

            if (url.pathname.endsWith('/') && path.startsWith('/')) {
              realPath = path.substring(1);
            } else if (!url.pathname.endsWith('/') && !path.startsWith('/')) {
              realPath = '/' + realPath;
            }

            url.pathname = realPath;
          }

          if (config.type === TYPE_PREVIEW) {
            const tokenResponse = await AuthenticationService.getResourceToken (workspace);
            url.searchParams.set ('previewData', btoa (JSON.stringify ({ alias, token: tokenResponse.token, workspace })));
          }

          return { url: url.toString () };
        }
      };
    }
  ]);
